import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import Bill from "../components/Bill";

const API = process.env.REACT_APP_API_URL;

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "N/A";

const ExporterDashboard = () => {
  const token = localStorage.getItem("token");

  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [hiddenOrders, setHiddenOrders] = useState(() => {
    const saved = localStorage.getItem("hiddenOrders");
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deliveryDates, setDeliveryDates] = useState({});
  const [imageMode, setImageMode] = useState("url"); // 👈 url or file
  const fileInputRef = useRef(null);                 // 👈 file ref

  const [productForm, setProductForm] = useState({
    name: "",
    price: "",
    discount: "",
    image: "",
    category: "",
    unit: "kg",
  });

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/exporter`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data);
    } catch {
      toast.error("Orders load nahi huye");
    }
  }, [token]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const hideOrder = (id) => {
    const updated = [...hiddenOrders, id];
    setHiddenOrders(updated);
    localStorage.setItem("hiddenOrders", JSON.stringify(updated));
    toast.success("Order removed from view!");
  };

  const confirmOrder = async (id) => {
    try {
      await axios.put(
        `${API}/orders/confirm/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order Completed");
      fetchOrders();
    } catch {
      toast.error("Confirm failed");
    }
  };

  const setDeliveryDate = async (orderId) => {
    const date = deliveryDates[orderId];
    if (!date) {
      toast.error("Pehle date select karo!");
      return;
    }
    try {
      await axios.put(
        `${API}/orders/set-delivery/${orderId}`,
        { deliveryDate: date },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Delivery date set ho gayi! ✅");
      fetchOrders();
    } catch {
      toast.error("Date set nahi hui");
    }
  };

  const handleProductChange = (e) => {
    setProductForm({ ...productForm, [e.target.name]: e.target.value });
  };

  // 👈 Gallery se image select karne par base64 convert
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setProductForm((prev) => ({ ...prev, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const addProduct = async () => {
    if (!productForm.category) {
      toast.error("Please select category");
      return;
    }
    if (!productForm.image) {
      toast.error("Please add product image");
      return;
    }
    try {
      await axios.post(`${API}/products`, productForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Product Added Successfully 🍎");
      setProductForm({
        name: "",
        price: "",
        discount: "",
        image: "",
        category: "",
        unit: "kg",
      });
      setImageMode("url");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch {
      toast.error("Product add nahi hua");
    }
  };

  const stats = {
    total: orders.length,
    placed: orders.filter((o) => o.status === "Placed").length,
    completed: orders.filter((o) => o.status === "Completed").length,
    cancelled: orders.filter((o) => o.status === "Cancelled").length,
  };

  const visibleOrders = orders.filter((o) => {
    if (hiddenOrders.includes(o._id)) return false;
    if (filter === "ALL") return true;
    if (filter === "Cancelled")
      return o.products.some((p) => p.status === "Cancelled");
    return o.status === filter;
  });

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="container-fluid bg-dark text-light min-vh-100 py-3">
      <Toaster />

      <h2 className="text-center fw-bold mb-4 fs-4">
        🚀 Exporter Dashboard
        <div className="text-muted fs-6 fw-normal">Manage products & orders</div>
      </h2>

      {/* ADD PRODUCT */}
      <div className="card shadow-lg border-0 mb-4">
        <div className="card-body">
          <h5 className="fw-bold text-primary mb-3">➕ Add New Product</h5>
          <div className="row g-2">

            {/* Name */}
            <div className="col-12 col-md-6">
              <input
                className="form-control form-control-sm"
                name="name"
                placeholder="Product Name"
                value={productForm.name}
                onChange={handleProductChange}
              />
            </div>

            {/* Price */}
            <div className="col-6 col-md-3">
              <input
                type="number"
                className="form-control form-control-sm"
                name="price"
                placeholder="Price / kg"
                value={productForm.price}
                onChange={handleProductChange}
              />
            </div>

            {/* Discount */}
            <div className="col-6 col-md-3">
              <input
                type="number"
                className="form-control form-control-sm"
                name="discount"
                placeholder="Discount %"
                value={productForm.discount}
                onChange={handleProductChange}
              />
            </div>

            {/* IMAGE - URL or Gallery */}
            <div className="col-12 col-md-6">
              {/* Toggle */}
              <div className="btn-group w-100 mb-2" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${imageMode === "url" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    setImageMode("url");
                    setProductForm((prev) => ({ ...prev, image: "" }));
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  🔗 URL
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${imageMode === "file" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    setImageMode("file");
                    setProductForm((prev) => ({ ...prev, image: "" }));
                  }}
                >
                  🖼️ Gallery
                </button>
              </div>

              {/* URL Input */}
              {imageMode === "url" && (
                <input
                  className="form-control form-control-sm"
                  name="image"
                  placeholder="Image URL paste karo"
                  value={productForm.image}
                  onChange={handleProductChange}
                />
              )}

              {/* File Input */}
              {imageMode === "file" && (
                <input
                  type="file"
                  accept="image/*"
                  className="form-control form-control-sm"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                />
              )}

              {/* Preview */}
              {productForm.image && (
                <img
                  src={productForm.image}
                  alt="preview"
                  className="mt-2 rounded"
                  style={{ height: "80px", objectFit: "cover", width: "100%" }}
                />
              )}
            </div>

            {/* Category */}
            <div className="col-12 col-md-6">
              <select
                className="form-select form-select-sm"
                name="category"
                value={productForm.category}
                onChange={handleProductChange}
              >
                <option value="">Select Category</option>
                <option value="apple"> Apple</option>
                <option value="orange"> Orange</option>
                <option value="grapes"> Grapes</option>
                <option value="banana">Banana</option>
                <option value="strawberry"> Strawberry</option>
                <option value="pomegranate">Pomegranate</option>
              </select>
            </div>

            {/* Submit */}
            <div className="col-12">
              <button className="btn btn-success w-100 btn-sm" onClick={addProduct}>
                ➕ Add Product
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="row g-2 mb-4">
        <div className="col-6 col-md-3">
          <div className="card text-center text-white bg-primary shadow-sm h-100" style={{ cursor: "pointer" }} onClick={() => setFilter("ALL")}>
            <div className="card-body p-2"><small>Total</small><h4>{stats.total}</h4></div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center bg-warning shadow-sm h-100" style={{ cursor: "pointer" }} onClick={() => setFilter("Placed")}>
            <div className="card-body p-2"><small>Placed</small><h4>{stats.placed}</h4></div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center text-white bg-success shadow-sm h-100" style={{ cursor: "pointer" }} onClick={() => setFilter("Completed")}>
            <div className="card-body p-2"><small>Completed</small><h4>{stats.completed}</h4></div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center text-white bg-danger shadow-sm h-100" style={{ cursor: "pointer" }} onClick={() => setFilter("Cancelled")}>
            <div className="card-body p-2"><small>Cancelled</small><h4>{stats.cancelled}</h4></div>
          </div>
        </div>
      </div>

      {/* ORDERS TABLE */}
      <div className="card shadow-lg border-0">
        <div className="card-body p-2">
          <div className="table-responsive">
            <table className="table table-dark table-hover table-sm align-middle">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Products</th>
                  <th>Status</th>
                  <th>🚚 Delivery Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center">No orders</td>
                  </tr>
                )}
                {visibleOrders.map((o) => (
                  <tr key={o._id}>
                    <td>#{o._id.slice(-6)}</td>
                    <td>
                      <b className="d-block">{o.customer?.name}</b>
                      <small className="d-block">📱 {o.customer?.phone}</small>
                      <small className="d-block text-muted">📍 {o.customer?.address}</small>
                    </td>
                    <td className="small lh-sm">
                      {o.products.map((p, i) => (
                        <div key={i}>
                          {p.name} ({p.quantity}{p.unit})
                          {p.status === "Cancelled" && (
                            <span className="text-danger ms-1">❌</span>
                          )}
                        </div>
                      ))}
                      <div className="fw-bold text-warning mt-1">₹{o.totalPrice}</div>
                    </td>
                    <td>
                      <span className={`badge rounded-pill px-3 py-1 ${
                        o.status === "Placed" ? "bg-warning text-dark"
                        : o.status === "Completed" ? "bg-success"
                        : "bg-danger"
                      }`}>
                        {o.status}
                      </span>
                    </td>
                    <td>
                      {o.deliveryDate ? (
                        <div>
                          <span className="text-success fw-semibold small">
                            ✅ {formatDate(o.deliveryDate)}
                          </span>
                          {o.status === "Placed" && (
                            <div className="mt-1 d-flex gap-1">
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                min={today}
                                style={{ width: "140px" }}
                                onChange={(e) =>
                                  setDeliveryDates({ ...deliveryDates, [o._id]: e.target.value })
                                }
                              />
                              <button
                                className="btn btn-outline-warning btn-sm"
                                onClick={() => setDeliveryDate(o._id)}
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                        </div>
                      ) : o.status === "Placed" ? (
                        <div className="d-flex gap-1">
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            min={today}
                            style={{ width: "140px" }}
                            onChange={(e) =>
                              setDeliveryDates({ ...deliveryDates, [o._id]: e.target.value })
                            }
                          />
                          <button
                            className="btn btn-info btn-sm"
                            onClick={() => setDeliveryDate(o._id)}
                          >
                            Set
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted small">N/A</span>
                      )}
                    </td>
                    <td className="bg-dark">
                      <div className="d-flex flex-wrap gap-1">
                        {o.status === "Placed" &&
                          o.products.some((p) => p.status !== "Cancelled") && (
                            <button
                              className="btn btn-success btn-sm px-2"
                              onClick={() => confirmOrder(o._id)}
                            >
                              ✔ Confirm
                            </button>
                          )}
                        {(o.status === "Completed" || o.status === "Cancelled") && (
                          <button
                            className="btn btn-outline-danger btn-sm px-2"
                            onClick={() => hideOrder(o._id)}
                          >
                            🗑
                          </button>
                        )}
                        <button
                          className="btn btn-outline-light btn-sm px-2"
                          onClick={() => setSelectedOrder(o)}
                        >
                          🧾
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedOrder && (
        <Bill order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
};

export default ExporterDashboard;